import {RiArrowDownSLine, RiSearchLine} from "react-icons/ri";
import React, {useState, Fragment, useEffect, useCallback, useMemo} from "react";
import {ModalHandler, OpenModalHandle} from "../modal/ModalHandler";
import {BASKET_FILTER_BUTTON_DATA} from "../../constants/component_constants";

const BasketFilterCard = ({onSearch, searchPerformed, resetTrigger}) => {
  const [isExpanded, setIsExpanded] = useState(true); // 카드 펼치기 여부
  const [filters, setFilters] = useState({  // 필터 조건 목록
    odds: null,  // 배당
    folder: null, // 폴더
    types: [],   // 유형
    sports: [], // 종목
    mode: "", // test 일때 임시데이터, live || null = 서버 데이터
  })
  const [searchTrigger, setSearchTrigger] = useState(0); // 검색 카운트 -> useEffect 트리거
  const OpenModal = useMemo(() => OpenModalHandle.create(), []);

  useEffect(() => {
    // resetTrigger 변경되면 필터 초기화
    setFilters({
      odds: null,
      folder: null,
      types: [],
      sports: [],
      mode: "",
    });
  }, [resetTrigger]);

  useEffect(() => {
    setIsExpanded(!searchPerformed);
  }, [searchTrigger, searchPerformed]);

  const handleOptionSelect = useCallback((key, optionValue, isMultiple) => {
    // 조건 선택
    if (isMultiple) {
      // 복수 선택
      setFilters((prev) => ({
        ...prev, [key]: prev[key].includes(optionValue)
          ? prev[key].filter((item) => item !== optionValue)
          : [...prev[key], optionValue],
      }));
    } else {
      // 단일 선택
      setFilters((prev) => ({
        ...prev, [key]: prev[key] === optionValue ? null : optionValue,
      }));
    }
  }, []);

  const isAllMenuSelect = useCallback(() => {
    // 모든 항목을 선택 했는지 검사
    return (
      Boolean(filters.odds) &&
      Boolean(filters.folder) &&
      filters.types.length > 0 &&
      filters.sports.length > 0
    );
  }, [filters]);

  const handleSearch = useCallback(() => {
    // 찾기
    if (!isAllMenuSelect()) {
      OpenModal.openWarningModal('조건 부족', '항목별로 최소 한 가지의 조건은 선택되어야 합니다.');
      return;
    }
    onSearch(filters);
    setSearchTrigger((prev) => prev + 1);
  }, [filters, isAllMenuSelect, onSearch]);

  const isSelected = useCallback((optionValue, isMultiple, selectedValue) => {
    // 복수 선택일때 -> 포함여부 확인
    if (isMultiple) {
      return Array.isArray(selectedValue) && selectedValue.includes(optionValue);
    }
    // 단일 선택일때 -> 옵션이랑 동일한지 확인
    return selectedValue === optionValue;
  }, []);

  const handleSelectAll = (menuKey, options) => {
    // 복수 선택 가능 옵션 전체 선택/해제 핸들러
    const currentValues = filters[menuKey];
    const optionValues = options.map(opt => opt.value);

    // 모두 선택되어 있으면 전체 해제, 아니면 전체 선택
    if (currentValues.length === optionValues.length) {
      setFilters(prev => ({...prev, [menuKey]: []}));
    } else {
      setFilters(prev => ({...prev, [menuKey]: [...optionValues]}));
    }
  };

  const isAllOptionSelected = (menuKey, options) => {
    // 옵션 전체 선택 여부 확인
    const currentValues = filters[menuKey];
    const optionValues = options.map(opt => opt.value);
    return currentValues.length === optionValues.length;
  };

  const getOptionLabel = (menuKey, value) => {
    const menu = BASKET_FILTER_BUTTON_DATA.find(m => m.key === menuKey);
    const option = menu?.options.find(opt => opt.value === value);
    return option?.label || value;
  };

  return (
    <div className="w-full border border-gray-300 shadow-lg bg-white p-5 rounded-xl">
      <ModalHandler initModalHandle={OpenModal}/>

      <div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex w-full items-center justify-between text-left transition-colors"
        >
          <div className="w-full flex flex-wrap items-center gap-3">
            <div className="w-full flex flex-row justify-between">
              <h2 className="text-2xl font-semibold text-foreground">필터 조건</h2>

              <div className="ml-3 flex-shrink-0">
                <RiArrowDownSLine
                  className={`h-5 w-5 text-muted-foreground transition-transform duration-500 ${
                    isExpanded ? 'rotate-0' : 'rotate-180'
                  }`}
                />
              </div>
            </div>

            {/* <-- 카드 접었을 때 : 선택한 조건 나열 --> */}
            {!isExpanded && isAllMenuSelect() && (
              <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center rounded-full bg-blue-800 px-3 py-1  shadow-md text-white">
                    배당: {getOptionLabel('odds', filters.odds)}
                  </span>

                <span className="inline-flex items-center rounded-full bg-blue-800 px-3 py-1 shadow-md text-white">
                    폴더: {getOptionLabel('folder', filters.folder)}
                  </span>

                {filters.types.map((type) => (
                  <span key={type}
                        className="inline-flex items-center rounded-full bg-cyan-700 px-3 py-1 shadow-md text-white"
                  >
                    {getOptionLabel('types', type)}
                  </span>
                ))}
                {filters.sports.map((sports) => (
                  <span key={sports}
                        className="inline-flex items-center rounded-full bg-sky-200 px-3 py-1 shadow-md"
                  >
                    {getOptionLabel('sports', sports)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </button>
      </div>

      {/* <-- 카드 펼쳤을 때 : 선택 조건 리스트 --> */}
      {isExpanded && (
        <div className="space-y-6 pt-4">
          {BASKET_FILTER_BUTTON_DATA.map((menu) => (
            <React.Fragment key={menu.key}>
              {menu.hasDivider && <div className="border-t border-gray-200"/>}

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-md font-semibold">{menu.title}</h3>
                  <span className="text-xs text-gray-600">
                    ({menu.isMultiple ? '복수 선택 가능' : '단일 선택'})
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">

                  {/* 복수 선택인 경우 전체 버튼 추가 */}
                  {menu.isMultiple && (
                    <button
                      onClick={() => handleSelectAll(menu.key, menu.options)}
                      className={`min-w-[56px] px-5 py-2 text-sm font-medium rounded-md shadow-md
                        ${isAllOptionSelected(menu.key, menu.options)
                        ? 'bg-blue-900 text-white'
                        : 'border border-slate-400 bg-slate-100 text-slate-900'}
                      `}
                    >
                      전체
                    </button>
                  )}

                  {menu.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleOptionSelect(menu.key, option.value, menu.isMultiple)}
                      className={`min-w-[56px] px-5 py-2 text-sm font-medium rounded-md shadow-md
                             ${isSelected(option.value, menu.isMultiple, filters[menu.key])
                        ? 'bg-blue-900 text-white' : 'border border-slate-400 bg-slate-100 text-slate-900'}
                             `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </React.Fragment>
          ))}

          {/* 초기화 + 찾기 버튼 */}
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-6">
            <button
              onClick={() =>
                setFilters({
                  odds: null,
                  folder: null,
                  types: [],
                  sports: [],
                })
              }
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-900"
            >
              초기화
            </button>
            <button
              onClick={handleSearch}
              className="px-4 py-2 text-sm font-semibold rounded-md bg-slate-900 text-white shadow-md flex items-center gap-2"
            >
              <RiSearchLine className="h-4 w-4"/>
              찾기
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
export default BasketFilterCard;